import React, { useEffect, useState } from "react";
import { Alert, Button, Table, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import Amount from "../shared/Amount";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText } from "../api";
import * as api from "./api";
import StatementDialog from "./StatementDialog";
import { DownloadButton, useRange } from "./receivablesUi";

/** RP-1 Debitoren-Saldenliste with the check against the trial balance and CB-4 statements. */
const DebitorenTab = () => {
  const { t } = useTranslation("accounting");
  const { params, picker } = useRange();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .getDebitoren(params)
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
  }, [params]);

  const name = `Debitoren-Saldenliste_${params.from}_${params.to}`;
  return (
    <div>
      <PageToolbar
        filters={picker}
        actions={
          <div className="acct-actions">
            <Button onClick={() => setStatement({ customerId: null })}>
              {t("Customer statement")}
            </Button>
            <DownloadButton
              label={t("Download CSV")}
              promise={() => api.downloadDebitoren(params, "csv")}
              filename={`${name}.csv`}
            />
            <DownloadButton
              label={t("Download PDF")}
              promise={() => api.downloadDebitoren(params, "pdf")}
              filename={`${name}.pdf`}
            />
          </div>
        }
      />
      {data ? (
        <Alert
          style={{ marginBottom: 12 }}
          showIcon
          type={data.verify?.ok ? "success" : "error"}
          message={
            data.verify?.ok
              ? t("The Debitoren balances match the trial balance of the receivables accounts.")
              : t("Difference to the trial balance of the receivables accounts")
          }
          description={
            data.verify?.ok ? null : <Amount cents={data.verify?.differences?.closing} strong />
          }
        />
      ) : null}
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        loading={loading}
        pagination={false}
        rowKey="account"
        dataSource={data?.rows || []}
        summary={() =>
          data ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <strong>{t("Total")}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="end">
                <Amount cents={data.totals.opening} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="end">
                <Amount cents={data.totals.debit} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="end">
                <Amount cents={data.totals.credit} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="end">
                <Amount cents={data.totals.closing} strong />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} />
            </Table.Summary.Row>
          ) : null
        }
        columns={[
          { title: t("Account"), dataIndex: "account", width: 90 },
          { title: t("Customer number"), dataIndex: "customerNumber", width: 110 },
          {
            title: t("Customer"),
            dataIndex: "name",
            render: (v, r) => (
              <span className="acct-wrap">
                {v} {r.creditBalance ? <Tag color="blue">{t("Credit balance")}</Tag> : null}
                {r.insolvent ? <Tag color="red">{t("Insolvent")}</Tag> : null}
              </span>
            )
          },
          {
            title: t("Opening balance"),
            dataIndex: "opening",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Debit"),
            dataIndex: "debit",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Credit"),
            dataIndex: "credit",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Closing balance"),
            dataIndex: "closing",
            align: "end",
            render: v => <Amount cents={v} strong />
          },
          {
            title: "",
            key: "actions",
            render: (_, r) =>
              r.customer ? (
                <Button size="small" onClick={() => setStatement({ customerId: r.customer })}>
                  {t("Statement")}
                </Button>
              ) : null
          }
        ]}
      />
      <StatementDialog
        open={Boolean(statement)}
        customerId={statement?.customerId}
        from={params.from}
        to={params.to}
        onClose={() => setStatement(null)}
      />
    </div>
  );
};

export default DebitorenTab;
